import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const DataRetention = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
      <main className="flex-grow bg-white py-8 md:py-12">
        <div className="container mx-auto px-2 md:px-4">
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8 md:mb-12">
              <h1 className="text-3xl font-bold mb-6 text-center text-[#2D0052]">
                Data Retention Policy
              </h1>
              <p className="text-sm text-gray-500 text-center mb-8">
                Last updated: Jun 02, 2025
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-base leading-relaxed text-gray-800 mb-8">
                This Data Retention Policy outlines how TTL Media Private
                Limited ("Oye Creators," "we," "us," or "our") collects,
                retains, archives, and deletes data through our online
                marketplace platform. We ensure compliance with Indian laws
                (Information Technology Act 2000, DPDP Act 2023) and
                international regulations (GDPR) and protect Oye Creators and
                its employees from legal liabilities.
              </p>

              <section className="mb-8">
                <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                  1. Purpose and Scope
                </h2>
                <p className="text-base leading-relaxed text-gray-800">
                  <strong>1.1 Purpose:</strong> Provide transparency about data
                  lifecycle management, comply with legal obligations, and
                  mitigate risks of unauthorized retention or deletion.
                </p>
                <p className="text-base leading-relaxed text-gray-800">
                  <strong>1.2 Scope:</strong> Applies to all data related to:
                </p>
                <p className="text-base leading-relaxed text-gray-800 ml-6">
                  Users: Enterprise Clients (Brands/Businesses), Creators, and
                  Platform Managers
                </p>
                <p className="text-base leading-relaxed text-gray-800 ml-6">
                  Data Types: Personal Data, Non-Personal Data, User-Generated
                  Content, Social Media Data, Transaction Records, Logs,
                  Backups, Communications, Analytics, Cookies
                </p>
                <p className="text-base leading-relaxed text-gray-800 ml-6">
                  Social Media Data: Information fetched from or about users on
                  third-party social networks (e.g., profile insights,
                  engagement metrics, follower demographics, public posts) via
                  API integrations or manual reports.
                </p>
                <p className="text-base leading-relaxed text-gray-800 ml-6">
                  Systems: Web and mobile applications, internal databases,
                  third-party services, backups
                </p>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  2. Data Categories & Retention Periods
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 mb-4">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Category
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Examples
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Retention
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Legal Basis / Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Registration & Profile Data
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Name, contact, identity proofs, KYC
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Active + 3 years after account closure
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Contractual necessity; Audit under IT SPDI Rule 3,
                          DPDP transitional provisions
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          User-Generated Content (UGC)
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Campaign posts, images, videos, scripts
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Until campaign end + 2 years archive; deletable on
                          request*
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          License lifecycle; dispute resolution; archived
                          encrypted
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Social Media Data
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          API-derived metrics (followers, reach, impressions),
                          public posts
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          1 year from collection or campaign end + 1 year
                          archive
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Platform insights; GDPR Art.5(1)(e) "storage
                          limitation"; user consent where required
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Transactions & Payments
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Invoices, receipts, payout logs
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          7 years
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Income-tax Act requirements; RBI guidelines
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Usage & Access Logs
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Login/logout, API calls
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          180 days (active); 1 year (archived)
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Security monitoring; forensics; GDPR Art.5(1)(e)
                          storage limitation
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          System Backups
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Full & incremental backups
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          30 days rolling
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Disaster recovery; overwritten older backups
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Cookies & Tracking Data
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Session, preference, analytics cookies
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          As per Cookie Policy (session to 24 months)
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          User consent under GDPR Art.6(1)(a); cleared on
                          opt-out
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Support & Communications
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Emails, chat transcripts, tickets
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Resolution + 5 years
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Customer dispute management
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Marketing Consents & Preferences
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Opt-in records, unsubscribes
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Opt-out + 3 years
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Consent record under GDPR Arts.7(1); DPDP consent
                          framework
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Legal & Compliance Records
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Audit trails, compliance certificates
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          10 years
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          Statutory requirements; legal defense
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm italic text-gray-600">
                  *Creators may request earlier deletion of their UGC; if a
                  client campaign is active, removal may be subject to contract
                  terms.
                </p>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  3. Data Storage & Security Measures
                </h2>
                <p className="mb-2">
                  <strong>Encryption:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">
                    Data at rest: AES-256 on all databases and archives.
                  </li>
                  <li className="mb-1">
                    Data in transit: TLS 1.2+ for all network communication.
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>Access Controls:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">
                    Role-based access (RBAC) with least-privilege.
                  </li>
                  <li className="mb-1">
                    Multi-factor authentication (MFA) for admins.
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>Network Security:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">Firewalls and intrusion detection.</li>
                  <li className="mb-1">
                    Regular vulnerability assessments and penetration tests.
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>Anonymization & Pseudonymization:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">
                    Backup anonymization for analytics archives.
                  </li>
                  <li className="mb-1">
                    Pseudonymization of user identifiers in research datasets.
                  </li>
                </ul>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  4. Deletion & Archival Procedures
                </h2>
                <p className="mb-2">
                  <strong>Automated Jobs:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">Daily: purge logs {">"}180 days.</li>
                  <li className="mb-1">
                    Monthly: archive UGC and social media data {">"}2 years to
                    cold storage.
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>Manual Requests:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">DSARs handled within 30 days.</li>
                  <li className="mb-1">
                    Erasure requests completed within 60 days, subject to legal
                    holds.
                  </li>
                </ul>

                <p className="mb-2">
                  <strong>Exception Holds:</strong>
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">
                    Ongoing litigation, investigations, or audits—retention
                    extended until resolution.
                  </li>
                </ul>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  5. International Transfers
                </h2>
                <p className="mb-4">
                  Primary processing and storage in India. Cross-border
                  transfers to processors (e.g., AWS Singapore) follow:
                </p>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">
                    Standard Contractual Clauses (SCCs) under GDPR.
                  </li>
                  <li className="mb-1">
                    Adequacy mechanisms per DPDP guidelines.
                  </li>
                </ul>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  6. Data Subject Rights & Contact
                </h2>
                <p className="mb-4">
                  Under GDPR and DPDP Act, individuals may:
                </p>
                <p className="mb-4">
                  Access, Rectify, Erase, Restrict, Object, Port their personal
                  data.
                </p>
                <p className="mb-4">Submit requests to:</p>
                <p className="mb-1 font-bold">Data Protection Officer</p>
                <p className="mb-1">Email: dpo@oyecreators.in</p>
                <p className="mb-4">Address: Quantum Works, CTS No. 177 p, metro station, S. No. 43/2, near Nal stop, Pandurang Colony, Erandwane, Pune, Maharashtra 411004</p>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  7. Compliance & Audits
                </h2>
                <ul className="list-disc pl-8 mb-4">
                  <li className="mb-1">
                    Annual external audits by certified firms.
                  </li>
                  <li className="mb-1">
                    Quarterly internal compliance reviews.
                  </li>
                  <li className="mb-1">
                    Policy versions logged; major updates trigger user
                    notification and re-consent if required.
                  </li>
                </ul>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  8. Liability & Indemnification
                </h2>
                <p className="mb-2">
                  <strong>Limitation of Liability:</strong> Oye Creators not
                  liable for data loss post-retention.
                </p>
                <p className="mb-2">
                  <strong>Indemnification:</strong> Users indemnify Oye
                  Creators/employees for claims from data misuse or unlawful
                  requests.
                </p>
              </section>

              <Separator className="my-8 bg-gray-200" />

              <section className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-oye-pink">
                  9. Policy Updates
                </h2>
                <p className="mb-4">
                  We may revise this policy for new laws or practices. Updated
                  versions will be published with a new "Last updated" date;
                  major changes require user re-consent where mandated.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
  )
}

export default DataRetention
